import type { YaasPositionCardData } from "@solomon-apps/shared";
import { useQueries } from "@tanstack/react-query";

import { withPositionAmount } from "./helpers";
import type { LpBalance } from "./lpTypes";
import { fetchRaydiumLpBalance } from "./raydiumLpBalance";

/**
 * Enrichment only: the API owns membership, lifecycle and reward history.
 * The RPC reader decodes Raydium CLMM accounts, so other protocols keep their amount unknown.
 */
export function useRaydiumLpBalances(
    positions: YaasPositionCardData[],
    walletAddress: string | null | undefined,
    enabled: boolean
): YaasPositionCardData[] {
    const lpPositions = positions.filter((position) => position.position.lpIdentity?.protocol === "raydium-clmm");
    const queries = useQueries({
        queries: lpPositions.map((position) => ({
            queryKey: ["yaas", "lp-balance", walletAddress, position.uuid, position.position.lpIdentity],
            queryFn: ({ signal }: { signal: AbortSignal }): Promise<LpBalance | null> =>
                fetchRaydiumLpBalance(position.position.lpIdentity!, walletAddress!, signal),
            enabled: enabled && Boolean(walletAddress),
            staleTime: 5 * 60_000,
            refetchInterval: 5 * 60_000,
            retry: 1
        }))
    });
    // Keep successful principal through RPC failures. A confirmed ownership loss returns null
    // and replaces cached data, so it still clears the displayed amount.
    const balances = new Map(lpPositions.map((position, index) => [position.uuid, queries[index].data]));
    return positions.map((position) => {
        const balance = balances.get(position.uuid);
        if (!balance || !enabled) return position;
        return withPositionAmount(position, balance.amount);
    });
}
